import { useState, useCallback, useEffect, useRef } from 'react'
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout'
import AnalyticsQuery from 'AnalyticsDashboard/components/AnalyticsQuery'
import HtmlBlock from 'AnalyticsDashboard/components/HtmlBlock'
import QueryBuilder from 'AnalyticsDashboard/components/QueryBuilder'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

const GRID_COLS = { lg: 12, md: 12, sm: 6, xs: 6 }
const DEFAULT_W = 6
const DEFAULT_H = 8
const ROW_HEIGHT = 50

function defaultLayoutItem(id, index, existingLayout) {
  const col = (index % 2) * DEFAULT_W
  const maxY = existingLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0)
  const row = index % 2 === 0 ? maxY : (existingLayout.length > 0 ? existingLayout[existingLayout.length - 1].y : 0)
  return { i: id, x: col, y: row, w: DEFAULT_W, h: DEFAULT_H, minW: 2, minH: 2 }
}

function initState(config) {
  if (!Array.isArray(config) || config.length === 0) return { queries: [], layout: [] }
  const queries = config.map((c, i) => {
    const id = c.id || `user_query_${Date.now()}_${i}`
    return { ...c, id }
  })
  const layout = queries.map((q, i) => {
    if (q.layout) return { ...q.layout, i: q.id, minW: 2, minH: 2 }
    return defaultLayoutItem(q.id, i, [])
  })
  return { queries, layout }
}

function toConfig(queries, layout) {
  const layoutMap = {}
  layout.forEach(item => {
    layoutMap[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h }
  })
  return queries.map(({ id, ...rest }) => ({
    ...rest,
    layout: layoutMap[id] || undefined,
  }))
}

export default function QueryBuilderPanel({ initialConfig, onChange }) {
  const [{ queries, layout }, setState] = useState(() => initState(initialConfig))
  const [showBuilder, setShowBuilder] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [viewModes, setViewModes] = useState({})
  const isInitialMount = useRef(true)
  const layoutMounted = useRef(false)
  const { width, containerRef } = useContainerWidth()

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (onChange) onChange(toConfig(queries, layout))
  }, [queries, layout])

  const setQueries = useCallback((updater) => {
    setState(prev => ({
      ...prev,
      queries: typeof updater === 'function' ? updater(prev.queries) : updater,
    }))
  }, [])

  const setLayout = useCallback((newLayout) => {
    setState(prev => ({ ...prev, layout: newLayout }))
  }, [])

  const handleAdd = useCallback((config) => {
    const id = `user_query_${Date.now()}`
    setState(prev => {
      const newQueries = [...prev.queries, { ...config, id }]
      const newLayoutItem = defaultLayoutItem(id, prev.queries.length, prev.layout)
      return {
        queries: newQueries,
        layout: [...prev.layout, newLayoutItem],
      }
    })
    setShowBuilder(false)
  }, [])

  const handleUpdate = useCallback((config) => {
    setQueries(prev => prev.map(q =>
      q.id === editingId ? { ...config, id: editingId } : q
    ))
    setEditingId(null)
    setShowBuilder(false)
  }, [editingId])

  const handleRemove = useCallback((id) => {
    setState(prev => ({
      queries: prev.queries.filter(q => q.id !== id),
      layout: prev.layout.filter(item => item.i !== id),
    }))
  }, [])

  const handleEdit = useCallback((id) => {
    setEditingId(id)
    setShowBuilder(true)
  }, [])

  const handleCancel = useCallback(() => {
    setShowBuilder(false)
    setEditingId(null)
  }, [])

  const handleLayoutChange = useCallback((newLayout) => {
    // Skip the first layout change fired during mount to avoid re-render while items attach
    if (!layoutMounted.current) {
      layoutMounted.current = true
      return
    }
    // Only accept layout items that correspond to current queries
    setState(prev => {
      const queryIds = new Set(prev.queries.map(q => q.id))
      const filtered = newLayout.filter(item => queryIds.has(item.i))
      return { ...prev, layout: filtered }
    })
  }, [])

  return (
    <div className="query-builder-panel" ref={containerRef}>
      {queries.length > 0 && width > 0 && (
        <ResponsiveGridLayout
          className="query-builder-panel__grid"
          width={width}
          layouts={{ lg: layout }}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          dragConfig={{ handle: '.query-builder-panel__drag-handle' }}
          onLayoutChange={handleLayoutChange}
          compactor={verticalCompactor}
          margin={[16, 16]}
        >
          {queries.map((config) => (
            <div key={config.id} className="query-builder-panel__query">
              <div className="query-builder-panel__query-header">
                <span className="query-builder-panel__drag-handle" title="Drag to reorder">⠿</span>
                <span className="query-builder-panel__query-title">
                  {config.name || (config.kind ? `${_.capitalize(config.kind)} - ${config.chartType}` : _.capitalize(config.chartType))}
                </span>
                <div className="query-builder-panel__query-actions">
                  {!config.static && (
                    <select
                      className="query-builder-panel__view-select"
                      value={viewModes[config.id] || 'chart'}
                      onChange={(e) => { const val = e.target.value; setViewModes(prev => ({ ...prev, [config.id]: val })) }}
                    >
                      <option value="chart">Chart</option>
                      <option value="table">Table</option>
                      <option value="sql">SQL</option>
                    </select>
                  )}
                  <button type="button" onClick={() => handleEdit(config.id)}>Edit</button>
                  <button type="button" onClick={() => handleRemove(config.id)}>Remove</button>
                </div>
              </div>
              <div className="query-builder-panel__query-body">
                {config.static ? (
                  <HtmlBlock chartOptions={config.chartOptions} />
                ) : (
                  <AnalyticsQuery
                    key={`${config.id}-${JSON.stringify(config)}`}
                    kind={config.kind}
                    chartType={config.chartType}
                    chartOptions={config.chartOptions}
                    metrics={config.metrics}
                    dimensions={config.dimensions}
                    primaryDimension={config.primaryDimension}
                    secondaryDimension={config.secondaryDimension}
                    orderBy={config.orderBy}
                    limit={config.limit}
                    overrideFilters={config.overrideFilters}
                    comparePeriods={config.comparePeriods}
                    viewMode={viewModes[config.id] || 'chart'}
                  />
                )}
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      <div className="query-builder-panel__toolbar">
        <button
          type="button"
          className="query-builder-panel__add"
          onClick={() => setShowBuilder(true)}
        >
          + Add Query
        </button>
      </div>

      {showBuilder && (
        <div className="query-builder-panel__modal-overlay" onClick={handleCancel}>
          <div className="query-builder-panel__modal" onClick={(e) => e.stopPropagation()}>
            <QueryBuilder
              key={editingId ? `edit-${editingId}` : 'add'}
              onSubmit={editingId ? handleUpdate : handleAdd}
              onCancel={handleCancel}
              initialConfig={editingId ? queries.find(q => q.id === editingId) : null}
            />
          </div>
        </div>
      )}
    </div>
  )
}
